import {
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@frontend-platform/ui-lib/ui';
import { Loader2, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useIsSuperAdmin } from '@/shared/hooks/use-is-super-admin';
import { getBotId, getCurrentUserId } from '../../../api/metrics-api';
import { useCalibrationConfig } from '../../../hooks/use-calibration-config';
import { useCalibrationEnvironments } from '../../../hooks/use-calibration-environments';
import { useCreateMetric, useDeleteMetric, useMetrics } from '../../../hooks/use-metrics';
import { type CalibrationMetric, getMetricId } from '../../../lib/types';
import {
  CUSTOM_METRIC_LIMIT_TOOLTIP,
  getCategoryMeta,
  METRIC_SIMILAR_TOAST_TITLE,
  METRIC_SUCCESS_TOAST,
  METRIC_TYPES,
} from '../constants';
import { AddCriterionModal, type CriterionCreatedEvent } from '../criteria/add-criterion-modal';
import { MetricCard } from '../criteria/metric-card';
import { RenameMetricModal } from '../criteria/rename-metric-modal';
import { type CreateRunFormData, ENVIRONMENT, type SelectedMetricGroup } from '../types';

interface CriteriaStepProps {
  formData: CreateRunFormData;
  setField: <K extends keyof CreateRunFormData>(key: K, value: CreateRunFormData[K]) => void;
}

interface MetricGroup {
  key: string;
  groupName: string;
  description: string;
  metrics: CalibrationMetric[];
}

// Normalise updatedAt (epoch seconds/ms or ISO string) to a comparable number.
function toTimestamp(value: string | number | undefined): number {
  if (!value) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : new Date(value).getTime() || 0;
}

// Predefined metrics first (original order), then customs by most-recently-updated.
function sortMetrics(list: CalibrationMetric[]): CalibrationMetric[] {
  const predefined = list.filter((m) => m.type !== METRIC_TYPES.CUSTOM);
  const custom = list
    .filter((m) => m.type === METRIC_TYPES.CUSTOM)
    .sort((a, b) => toTimestamp(b.updatedAt) - toTimestamp(a.updatedAt));
  return [...predefined, ...custom];
}

// Rename/Delete are exposed to the metric's creator, plus super admins —
// matching aistudio's `useMetricPermissions().canManage()`. Predefined metrics
// never expose these actions, for anyone.
function canManageMetric(metric: CalibrationMetric, isSuperAdmin: boolean): boolean {
  if (metric.type !== METRIC_TYPES.CUSTOM) return false;
  if (isSuperAdmin) return true;
  const creatorId = typeof metric.createdBy === 'string' ? metric.createdBy : metric.createdBy?.id;
  const currentUserId = getCurrentUserId();
  return !!creatorId && !!currentUserId && String(creatorId) === String(currentUserId);
}

export function CriteriaStep({ formData, setField }: CriteriaStepProps) {
  const { data: metrics = [], isLoading } = useMetrics();
  const createMetric = useCreateMetric();
  const deleteMetricMutation = useDeleteMetric();
  const { resolveBotRefId } = useCalibrationEnvironments();
  const { calibrationConfig } = useCalibrationConfig();
  const isSuperAdmin = useIsSuperAdmin();

  // Per-bot cap on custom criteria, from the CALIBRATION_CONFIG bot property.
  // Counted across every category, matching CriteriaSelection.vue — the limit
  // is per bot, not per metric group.
  const customMetricLimitReached = useMemo(
    () =>
      metrics.filter((m) => m.type === METRIC_TYPES.CUSTOM).length >=
      calibrationConfig.customMetricLimit,
    [metrics, calibrationConfig.customMetricLimit]
  );

  const [showAddModal, setShowAddModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState<CalibrationMetric | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CalibrationMetric | null>(null);

  const metricGroups = useMemo<MetricGroup[]>(() => {
    const byCategory: Record<string, CalibrationMetric[]> = {};
    for (const m of metrics) {
      const bucket = byCategory[m.category] ?? [];
      bucket.push(m);
      byCategory[m.category] = bucket;
    }
    return Object.entries(byCategory).map(([key, list]) => ({
      key,
      ...getCategoryMeta(key),
      metrics: sortMetrics(list),
    }));
  }, [metrics]);

  // Selection state lives in formData.selectedMetrics; project it into a
  // per-category set of stable metric ids.
  const selectionByGroup = useMemo<Record<string, Set<string>>>(() => {
    const acc: Record<string, Set<string>> = {};
    for (const group of formData.selectedMetrics ?? []) {
      acc[group.category] = new Set(group.metrics.map((m) => m.metricKey || m.id));
    }
    return acc;
  }, [formData.selectedMetrics]);

  // Build the canonical SelectedMetricGroup[] payload: full metric objects,
  // mirroring buildPayload in CriteriaSelection.vue.
  const buildPayload = (selection: Record<string, Set<string>>): SelectedMetricGroup[] =>
    metricGroups.map((group) => {
      const selectedIds = selection[group.key] ?? new Set<string>();
      const selected = group.metrics.filter((m) => selectedIds.has(getMetricId(m)));
      return {
        category: group.key,
        metrics: selected.map((m) => ({
          id: m.id,
          displayName: m.displayName,
          metricKey: m.metricKey ?? null,
          type: m.type,
          metadata: m.metadata ?? {},
        })),
        selectedCriteriaCount: selected.length,
        totalCriteriaCount: group.metrics.length,
      };
    });

  const cloneSelection = (): Record<string, Set<string>> => {
    const next: Record<string, Set<string>> = {};
    for (const [k, v] of Object.entries(selectionByGroup)) next[k] = new Set(v);
    return next;
  };

  const writeSelection = (next: Record<string, Set<string>>) =>
    setField('selectedMetrics', buildPayload(next));

  const toggleMetric = (group: MetricGroup, metric: CalibrationMetric) => {
    const id = getMetricId(metric);
    const next = cloneSelection();
    const set = next[group.key] ?? new Set<string>();
    if (set.has(id)) set.delete(id);
    else set.add(id);
    next[group.key] = set;
    writeSelection(next);
  };

  const toggleGroupAll = (group: MetricGroup, checked: boolean) => {
    const next = cloneSelection();
    next[group.key] = checked ? new Set(group.metrics.map(getMetricId)) : new Set<string>();
    writeSelection(next);
  };

  // ── Add / create ───────────────────────────────────────────────────────────
  const handleCreated = async ({
    metric,
    outcome,
    similarMetricName,
    message,
  }: CriterionCreatedEvent) => {
    let created: CalibrationMetric;
    try {
      created = await createMetric.mutateAsync(metric);
    } catch {
      // Error already surfaced by the query client's mutation handler.
      return;
    }
    setShowAddModal(false);

    // Auto-select the newly created metric for this run.
    const next = cloneSelection();
    const set = next[created.category] ?? new Set<string>();
    set.add(getMetricId(created));
    next[created.category] = set;
    // buildPayload reads metricGroups from the query cache which already
    // includes `created` (mutation onSuccess appends it synchronously); still,
    // compute against the updated list to avoid a race on slow re-renders.
    const groups = metricGroups.some((g) => g.key === created.category)
      ? metricGroups
      : [
          ...metricGroups,
          { key: created.category, ...getCategoryMeta(created.category), metrics: [] },
        ];
    setField(
      'selectedMetrics',
      groups.map((group) => {
        const list = group.key === created.category ? [...group.metrics, created] : group.metrics;
        const deduped = list.filter(
          (m, i, arr) => arr.findIndex((x) => getMetricId(x) === getMetricId(m)) === i
        );
        const selectedIds = next[group.key] ?? new Set<string>();
        const selected = deduped.filter((m) => selectedIds.has(getMetricId(m)));
        return {
          category: group.key,
          metrics: selected.map((m) => ({
            id: m.id,
            displayName: m.displayName,
            metricKey: m.metricKey ?? null,
            type: m.type,
            metadata: m.metadata ?? {},
          })),
          selectedCriteriaCount: selected.length,
          totalCriteriaCount: deduped.length,
        };
      })
    );

    if (outcome === 'SUCCESS_SIMILAR') {
      toast.warning(METRIC_SIMILAR_TOAST_TITLE, {
        description: message || (similarMetricName ? `Similar to "${similarMetricName}".` : ''),
        duration: 6000,
      });
    } else {
      toast.success(METRIC_SUCCESS_TOAST.title, {
        description: METRIC_SUCCESS_TOAST.description,
        duration: 6000,
      });
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const confirmDelete = async () => {
    const metric = deleteTarget;
    if (!metric) return;
    try {
      await deleteMetricMutation.mutateAsync(metric.id);
      // Deselect if it was selected in this wizard.
      const id = getMetricId(metric);
      if (selectionByGroup[metric.category]?.has(id)) {
        const next = cloneSelection();
        next[metric.category]?.delete(id);
        writeSelection(next);
      }
      setDeleteTarget(null);
    } catch {
      // Keep the dialog open so the user can retry; the error is already
      // surfaced by the query client's mutation handler.
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] w-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="flex min-h-[200px] w-full flex-col gap-4">
        {metricGroups.map((group) => {
          const selected = selectionByGroup[group.key] ?? new Set<string>();
          const total = group.metrics.length;
          const allChecked = total > 0 && selected.size === total;
          const indeterminate = selected.size > 0 && selected.size < total;

          return (
            <section key={group.key} className="rounded-lg border border-border bg-background p-4">
              <div className="flex items-start justify-between gap-4">
                {/* biome-ignore lint/a11y/noLabelWithoutControl: Radix Checkbox renders as button, not native input */}
                <label className="flex cursor-pointer items-start gap-3">
                  <Checkbox
                    className="mt-0.5"
                    checked={indeterminate ? 'indeterminate' : allChecked}
                    onCheckedChange={(value) => toggleGroupAll(group, value === true)}
                  />
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2 font-semibold text-foreground text-sm">
                      <span>{group.groupName}</span>
                      <span className="font-semibold text-gray-400 text-xs">
                        ({selected.size}/{total})
                      </span>
                    </div>
                    <p className="text-gray-500 text-xs">{group.description}</p>
                  </div>
                </label>

                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* Wrapper span keeps the tooltip reachable: a disabled
                        button fires no pointer events of its own. */}
                    <span className="shrink-0">
                      <button
                        type="button"
                        disabled={customMetricLimitReached}
                        onClick={() => setShowAddModal(true)}
                        className="inline-flex h-[30px] shrink-0 items-center gap-1.5 rounded-lg border border-border bg-background px-2 font-medium text-foreground text-xs hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-background"
                      >
                        <Plus className="h-3 w-3" />
                        Add Criterion
                      </button>
                    </span>
                  </TooltipTrigger>
                  {customMetricLimitReached && (
                    <TooltipContent className="text-xs">
                      {CUSTOM_METRIC_LIMIT_TOOLTIP}
                    </TooltipContent>
                  )}
                </Tooltip>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {group.metrics.map((metric) => (
                  <MetricCard
                    key={getMetricId(metric)}
                    metric={metric}
                    checked={selected.has(getMetricId(metric))}
                    canManage={canManageMetric(metric, isSuperAdmin)}
                    onToggle={() => toggleMetric(group, metric)}
                    onRename={setRenameTarget}
                    onDelete={setDeleteTarget}
                  />
                ))}
              </div>
            </section>
          );
        })}

        {/* The limit is re-checked here, not just on the button: the metric
            list can grow while the modal is open (another tab, a slow create),
            and the generation pipeline must not run past the cap. */}
        {showAddModal && !customMetricLimitReached && (
          <AddCriterionModal
            context={{
              botId: getBotId() || null,
              // The validate pipeline always runs against the bot's SANDBOX
              // instance, regardless of which env this run will evaluate —
              // `formData.evaluationBotRefId` can be any EP ladder stage
              // (QA/UAT/…) and must not be used here.
              botRefId: resolveBotRefId(ENVIRONMENT.SANDBOX),
              env: ENVIRONMENT.SANDBOX,
            }}
            existingMetrics={metrics}
            onCreated={handleCreated}
            onClose={() => setShowAddModal(false)}
          />
        )}

        {renameTarget && (
          <RenameMetricModal
            metric={renameTarget}
            existingMetrics={metrics}
            onRenamed={({ id }) => {
              // useUpdateMetric already patched the metrics query cache with the
              // new displayName by the time this fires, so rebuilding the payload
              // from metricGroups (unchanged id set) picks up the new name —
              // same pattern confirmDelete uses for the equivalent removal case.
              if (selectionByGroup[renameTarget.category]?.has(id)) {
                writeSelection(cloneSelection());
              }
              setRenameTarget(null);
            }}
            onClose={() => setRenameTarget(null)}
          />
        )}

        <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <DialogContent className="max-w-md">
            <div className="flex flex-col gap-1.5">
              <DialogTitle className="font-medium text-base text-foreground">
                Delete this criterion
              </DialogTitle>
              <DialogDescription className="text-muted-foreground text-sm">
                Deleting "{deleteTarget?.displayName}" cannot be undone.
              </DialogDescription>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="rounded-lg border border-border bg-background px-4 py-2 font-medium text-foreground text-sm hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleteMetricMutation.isPending}
                onClick={confirmDelete}
                className="rounded-lg bg-neutral-900 px-4 py-2 font-medium text-sm text-white hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {deleteMetricMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
